import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, ExternalLink, MessagesSquare } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, SearchInput, EmptyState, Badge } from "@/components/ui-em";

export default function Telegram() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/telegram/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/telegram/channels", { params: { q: q || undefined, category: category !== "all" ? category : undefined } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [q, category]);

  return (
    <div data-testid="telegram-page" className="space-y-8">
      <PageHeader
        eyebrow={t("dashboard.module")}
        title={t("telegram.title")}
        description={t("telegram.description")}
      />

      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1">
          <SearchInput value={q} onChange={setQ} placeholder={t("telegram.searchPlaceholder")} />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            onClick={() => setCategory("all")}
            className={`shrink-0 px-4 py-2 rounded-lg text-xs font-medium ${
              category === "all" ? "bg-gold text-ink-0" : "bg-white/5 text-zinc-400 hover:bg-white/10"
            }`}
            style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }}
            data-testid="telegram-cat-all"
          >
            {t("common.all")}
          </button>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`shrink-0 px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${
                category === c ? "bg-gold text-ink-0" : "bg-white/5 text-zinc-400 hover:bg-white/10"
              }`}
              style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }}
              data-testid={`telegram-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={MessagesSquare}
          title={t("telegram.noChannels")}
          description={t("telegram.noChannelsDesc")}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <a
              key={item.id}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`telegram-channel-${item.id}`}
              className="em-card em-card-hover p-6 flex flex-col gap-4 group"
              style={{ transitionProperty: "background-color, border-color, transform", transitionDuration: "180ms" }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="w-11 h-11 rounded-lg bg-[#229ED9]/15 border border-[#229ED9]/30 flex items-center justify-center shrink-0">
                  <Send className="w-5 h-5 text-[#229ED9]" strokeWidth={1.75} />
                </div>
                <Badge variant="outline" size="xs" uppercase>{item.category}</Badge>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg text-white group-hover:text-gold truncate" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>
                  {item.name}
                </div>
                {item.description && (
                  <p className="text-sm text-zinc-500 mt-2 line-clamp-3 leading-relaxed">{item.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-500 group-hover:text-gold" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>
                <span className="truncate">{item.link.replace(/^https?:\/\//, "")}</span>
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
